import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Trophy, Flame, Crown, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";

import { BACKEND_URL, API } from "../config/api";

const HallOfFame = () => {
  const navigate = useNavigate();
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/premium/hall-of-fame`)
      .then(res => setChampions(res.data.champions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="hall-of-fame-page">
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <h1 className="text-lg font-bold text-foreground">Hall of Fame</h1>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium text-yellow-500">Century Champions</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Explorers who achieved a 100+ day visit streak.<br />
            A permanent place of honor.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
          </div>
        ) : champions.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-2xl">
            <Trophy className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
            <p className="text-lg font-semibold text-foreground mb-2">No Century Champions Yet</p>
            <p className="text-sm text-muted-foreground">
              Be the first to reach a 100-day streak<br />and earn your place in the Hall of Fame.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {champions.map((champ, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-yellow-500/5 to-amber-500/5 border border-yellow-500/20 rounded-2xl p-5 flex items-center gap-4"
              >
                <div className="relative">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center">
                    {champ.profile_picture_path ? (
                      <img
                        src={`${API}/profile/picture/${champ.profile_picture_path}`}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover streak-flame-frame"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-2xl font-bold text-white">{champ.name?.charAt(0)?.toUpperCase()}</span>
                    )}
                  </div>
                  <Trophy className="w-5 h-5 text-yellow-500 absolute -bottom-1 -right-1 bg-background rounded-full p-0.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <p className="text-base font-semibold text-foreground truncate">{champ.name}</p>
                    {champ.founding_explorer && <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                    {champ.streak_legend_title && <span className="text-xs px-1.5 py-0.5 bg-pink-500/20 text-pink-500 rounded-full flex-shrink-0">Legend</span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-orange-500" /> Best: {champ.longest_streak} days</span>
                    {champ.inducted && <span>Inducted {new Date(champ.inducted).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</span>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HallOfFame;
